import { apiEndpoints } from "@config/env";
import { getData, isGetDataCancelled } from "../../../utils/global";
import { debug as logDebug, error as logError } from "../../../utils/console";
import type {
  FleetRankingBucket,
  FleetRankingDisplayMode,
  FleetRankingGroupBy,
  FleetRankingSeries,
  FleetRankingSeriesPoint,
} from "./fleetRankingTrendApi";
import {
  buildFleetRankingBuckets,
  buildFleetRankingTwsBuckets,
  buildRankByBucket,
  dedupeFleetRankingRows,
  fleetRankingSourceKey,
  FLEET_RANKING_CANCELLED_MESSAGE,
  type FleetRankingBucketRanks,
} from "./fleetRankingTrendApi";

export type FleetRankingPrestartMetric = "prestart_dist" | "dtl_start";

/** Value-mode y-axis upper cap (m) for prestart fleet ranking charts. */
export const FLEET_PRESTART_VALUE_AXIS_MAX: Record<FleetRankingPrestartMetric, number> = {
  prestart_dist: 300,
  dtl_start: 100,
};

/** Value-mode y-axis lower cap (m) for prestart fleet ranking charts. */
export const FLEET_PRESTART_VALUE_AXIS_MIN = -50;

export function fleetRankingPrestartValueAxisMax(metric: FleetRankingPrestartMetric): number {
  return FLEET_PRESTART_VALUE_AXIS_MAX[metric];
}

export function fleetRankingPrestartValueAxisMin(): number {
  return FLEET_PRESTART_VALUE_AXIS_MIN;
}

export type FleetRankingRankDirection = "high" | "low";

export interface FleetRankingPrestartTrendRow {
  source_name: string;
  bucket_key: string;
  bucket_label: string;
  bucket_order: string | number | null;
  avg_value: number | null;
  /** Mean of per-start ranks in this bucket. 1 is best. Null when the starts had no fleet to rank. */
  avg_rank: number | null;
  sample_count: number;
}

export type FleetRankingPrestartRowsByMetric = Record<
  FleetRankingPrestartMetric,
  FleetRankingPrestartTrendRow[]
>;

function hasPrestartSamples(sampleCount: number): boolean {
  return sampleCount > 0;
}

export interface FetchFleetRankingPrestartTrendParams {
  className: string;
  projectId: string | number;
  startDate: string;
  endDate: string;
  groupBy: FleetRankingGroupBy;
  sourceNames?: string[];
  signal?: AbortSignal;
  /** Required when group_by is day: scopes the drill-down to this event name. */
  event?: string;
}

function normalizeDateYmd(value: string): string {
  return String(value ?? "").replace(/[-/]/g, "").trim();
}

function parseFleetRankingPrestartTrendRows(raw: unknown[]): FleetRankingPrestartTrendRow[] {
  return raw.map((row) => {
    const r = row as Record<string, unknown>;
    const avgRaw = r.avg_value;
    const avg = avgRaw == null || avgRaw === "" ? null : Number(avgRaw);
    const rankRaw = r.avg_rank;
    const rank = rankRaw == null || rankRaw === "" ? null : Number(rankRaw);
    return {
      source_name: String(r.source_name ?? ""),
      bucket_key: String(r.bucket_key ?? ""),
      bucket_label: String(r.bucket_label ?? r.bucket_key ?? ""),
      bucket_order: (r.bucket_order as string | number | null) ?? null,
      avg_value: Number.isFinite(avg) ? avg : null,
      avg_rank: Number.isFinite(rank) ? rank : null,
      sample_count: Number(r.sample_count ?? 0),
    };
  });
}

export async function fetchFleetRankingPrestartTrendAll(
  params: FetchFleetRankingPrestartTrendParams
): Promise<
  { ok: true; rowsByMetric: FleetRankingPrestartRowsByMetric } | { ok: false; message: string }
> {
  const startYmd = normalizeDateYmd(params.startDate);
  const endYmd = normalizeDateYmd(params.endDate);
  if (startYmd.length !== 8 || endYmd.length !== 8) {
    return { ok: false, message: "Start and end dates are required" };
  }

  let url =
    `${apiEndpoints.app.data}/fleet-ranking-prestart-trend?class_name=${encodeURIComponent(params.className)}` +
    `&project_id=${encodeURIComponent(String(params.projectId))}` +
    `&start_date=${encodeURIComponent(startYmd)}` +
    `&end_date=${encodeURIComponent(endYmd)}` +
    `&group_by=${encodeURIComponent(params.groupBy)}`;

  if (params.sourceNames != null && params.sourceNames.length > 0) {
    url += `&source_names=${encodeURIComponent(JSON.stringify(params.sourceNames))}`;
  }

  if (params.event != null && params.event.trim() !== "") {
    url += `&event=${encodeURIComponent(params.event.trim())}`;
  }

  try {
    const result = await getData(url, params.signal);
    if (params.signal?.aborted || isGetDataCancelled(result)) {
      return { ok: false, message: FLEET_RANKING_CANCELLED_MESSAGE };
    }
    if (!result?.success) {
      return { ok: false, message: result?.message ?? "Failed to load fleet prestart trend" };
    }
    const payload = (result.data ?? {}) as {
      rows_by_metric?: Partial<Record<FleetRankingPrestartMetric, unknown[]>>;
    };
    const rawByMetric = payload.rows_by_metric ?? {};
    const rowsByMetric: FleetRankingPrestartRowsByMetric = {
      prestart_dist: parseFleetRankingPrestartTrendRows(
        Array.isArray(rawByMetric.prestart_dist) ? rawByMetric.prestart_dist : []
      ),
      dtl_start: parseFleetRankingPrestartTrendRows(
        Array.isArray(rawByMetric.dtl_start) ? rawByMetric.dtl_start : []
      ),
    };
    const totalRows = Object.values(rowsByMetric).reduce((sum, rows) => sum + rows.length, 0);
    logDebug("fleetRankingPrestart: fetched batch", {
      groupBy: params.groupBy,
      totalRows,
    });
    return { ok: true, rowsByMetric };
  } catch (err) {
    if (params.signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) {
      return { ok: false, message: FLEET_RANKING_CANCELLED_MESSAGE };
    }
    logError("fleetRankingPrestart: batch fetch failed", err);
    return {
      ok: false,
      message: err instanceof Error ? err.message : "Failed to load fleet prestart trend",
    };
  }
}

export function buildFleetRankingPrestartSeries(
  rawRows: FleetRankingPrestartTrendRow[],
  displayMode: FleetRankingDisplayMode,
  rankDirection: FleetRankingRankDirection,
  groupBy?: FleetRankingGroupBy,
  bucketOverride?: FleetRankingBucket[]
): { buckets: FleetRankingBucket[]; series: FleetRankingSeries[]; rankedFromStarts: boolean } {
  const rows = dedupeFleetRankingRows(rawRows);
  const buckets =
    bucketOverride != null && rows.length > 0
      ? bucketOverride
      : groupBy === "tws_bin" && rows.length > 0
        ? buildFleetRankingTwsBuckets()
      : buildFleetRankingBuckets(
          rows.map((row) => ({
            source_name: row.source_name,
            bucket_key: row.bucket_key,
            bucket_label: row.bucket_label,
            bucket_order: row.bucket_order,
            avg_vmg_percent: row.avg_value,
            sample_count: row.sample_count,
          }))
        );

  // Keyed on the normalized name; `displayName` keeps the original for labels and source colors.
  const bySource = new Map<
    string,
    { displayName: string; rowsByBucket: Map<string, FleetRankingPrestartTrendRow> }
  >();
  for (const row of rows) {
    const source = row.source_name.trim();
    if (!source || !row.bucket_key) continue;
    const key = fleetRankingSourceKey(source);
    if (!bySource.has(key)) {
      bySource.set(key, { displayName: source, rowsByBucket: new Map() });
    }
    bySource.get(key)!.rowsByBucket.set(row.bucket_key, row);
  }

  // Prefer the mean of per-start ranks, then order teams by that mean so the chart is
  // 1..N for the boats in the bucket. Falling back ranks the bucket's average metres,
  // which is what older responses (no avg_rank) still contain. A lower mean place is better.
  const rankedFromStarts = rows.some((r) => r.avg_rank != null && Number.isFinite(r.avg_rank));
  const rankByBucket =
    displayMode === "ranking"
      ? buildRankByBucket(
          rows
            .filter((r) => {
              if (!hasPrestartSamples(r.sample_count)) return false;
              if (rankedFromStarts) {
                return r.avg_rank != null && Number.isFinite(r.avg_rank);
              }
              return r.avg_value != null && Number.isFinite(r.avg_value);
            })
            .map((r) => ({
              bucketKey: r.bucket_key,
              sourceKey: fleetRankingSourceKey(r.source_name),
              value: (rankedFromStarts ? r.avg_rank : r.avg_value) as number,
            })),
          rankedFromStarts ? "low" : rankDirection
        )
      : new Map<string, FleetRankingBucketRanks>();

  const series: FleetRankingSeries[] = [...bySource.entries()]
    .sort(([, a], [, b]) => a.displayName.localeCompare(b.displayName))
    .map(([sourceKey, { displayName, rowsByBucket }]) => {
      const points: FleetRankingSeriesPoint[] = buckets.map((bucket) => {
        const row = rowsByBucket.get(bucket.key);
        const avg = row?.avg_value ?? null;
        const sampleCount = row?.sample_count ?? 0;
        const bucketRanks = rankByBucket.get(bucket.key);
        const rank = bucketRanks?.rankBySource.get(sourceKey) ?? null;
        const hasEnoughSamples = hasPrestartSamples(sampleCount);
        const yValue =
          !hasEnoughSamples
            ? null
            : displayMode === "ranking"
              ? rank
              : avg != null && Number.isFinite(avg)
                ? avg
                : null;
        return {
          bucketKey: bucket.key,
          bucketLabel: bucket.label,
          bucketOrder: bucket.order,
          yValue,
          avgVmgPercent: avg,
          sampleCount,
          rankPoolSize: bucketRanks?.poolSize ?? 0,
        };
      });
      return { sourceName: displayName, points };
    });

  return { buckets, series, rankedFromStarts };
}

export function fleetRankingPrestartMetricLabel(metric: FleetRankingPrestartMetric): string {
  switch (metric) {
    case "prestart_dist":
      return "Distance traveled to M1 in first 10 seconds";
    case "dtl_start":
      return "Distance to Line at Start";
    default:
      return metric;
  }
}

export function fleetRankingPrestartRankDirection(
  metric: FleetRankingPrestartMetric
): FleetRankingRankDirection {
  return metric === "prestart_dist" ? "high" : "low";
}

export function fleetRankingPrestartValueAxisLabel(metric: FleetRankingPrestartMetric): string {
  switch (metric) {
    case "prestart_dist":
      return "Distance traveled to M1 in first 10 seconds [m]";
    case "dtl_start":
      return "Distance to Line at Start [m]";
    default:
      return "Value";
  }
}

export function fleetRankingPrestartValueTooltipLabel(metric: FleetRankingPrestartMetric): string {
  switch (metric) {
    case "prestart_dist":
      return "Distance traveled to M1 in first 10 seconds [m]";
    case "dtl_start":
      return "Distance to Line at Start [m]";
    default:
      return "Value";
  }
}
